import { useState } from 'react';
import { adminLLMApi } from '../../../services/api/admin';
import type { JailbreakPresetListItem } from '../../../services/types';

interface PresetManagerModalProps {
  isOpen: boolean;
  onClose: () => void;
  presets: JailbreakPresetListItem[];
  onDeleted: () => void;
}

export function PresetManagerModal({
  isOpen,
  onClose,
  presets,
  onDeleted,
}: PresetManagerModalProps) {
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleDelete = async (id: string, name: string, isDefault: boolean) => {
    if (isDefault) {
      setError('無法刪除預設 Preset');
      return;
    }

    if (!confirm(`確定要刪除「${name}」嗎？`)) return;

    try {
      setDeletingId(id);
      setError(null);
      await adminLLMApi.deleteJailbreakPreset(id);
      onDeleted();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete preset');
    } finally {
      setDeletingId(null);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal">
      <div className="overlay" onClick={onClose} />
      <div className="modal-content max-w-lg">
        <div className="card-header flex items-center justify-between">
          <h3 className="heading-card text-neutral-900 dark:text-neutral-50">
            管理 Preset
          </h3>
          <button onClick={onClose} className="btn btn-ghost btn-icon">
            ✕
          </button>
        </div>
        <div className="card-body">
          {error && (
            <div className="mb-4 p-2 bg-danger-50 dark:bg-danger-900/20 text-danger-700 dark:text-danger-300 rounded text-sm">
              {error}
            </div>
          )}
          {presets.length === 0 ? (
            <p className="text-neutral-500 dark:text-neutral-400 text-center py-8">
              沒有 Preset
            </p>
          ) : (
            <div className="space-y-2">
              {presets.map((preset) => (
                <div
                  key={preset.id}
                  className="flex items-center justify-between p-3 bg-neutral-50 dark:bg-neutral-700/50 rounded-button"
                >
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-neutral-900 dark:text-neutral-50">
                        {preset.name}
                      </span>
                      {preset.is_default && (
                        <span className="badge badge-primary">預設</span>
                      )}
                    </div>
                    {preset.description && (
                      <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">
                        {preset.description}
                      </p>
                    )}
                  </div>
                  <button
                    onClick={() =>
                      handleDelete(preset.id, preset.name, preset.is_default)
                    }
                    disabled={deletingId === preset.id || preset.is_default}
                    className={`btn btn-ghost btn-sm ${
                      preset.is_default
                        ? 'opacity-50 cursor-not-allowed'
                        : 'text-danger-600 dark:text-danger-400 hover:bg-danger-50 dark:hover:bg-danger-900/20'
                    }`}
                  >
                    {deletingId === preset.id ? '刪除中...' : '刪除'}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="card-footer flex justify-end">
          <button onClick={onClose} className="btn btn-secondary btn-sm">
            關閉
          </button>
        </div>
      </div>
    </div>
  );
}
